import React, { memo, useRef, useState } from 'react'
import styles from './styles.module.scss'
import cn from 'classnames'

import { ReactComponent as TrashIcon } from '../../../assets/icons/trashIcon.svg'
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-white.svg'

import Switcher from '../../../UI/Switcher/Switcher'
import UseSmartTooltip from '../../../hooks/useSmartTooltip'

const RelativeContactsGroupItem = memo(({ data, setMain, isActive, onDelete, onEdit, isSwiped, onSwipeChange }) => {
  const headerTitle = data?.header?.filter(Boolean).join(', ')
  const mainLabel = data?.main_label || 'Next of kin'

  const [uncontrolledIsSwiped, setUncontrolledIsSwiped] = useState(false)
  const isSwipedValue = typeof isSwiped === 'boolean' ? isSwiped : uncontrolledIsSwiped
  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    pointerId: null,
    isTracking: false,
    isHorizontal: false,
  })

  const isInteractiveTarget = (target) => {
    if (!target?.closest) return false
    return Boolean(target.closest('button, a, input, label, textarea, select'))
  }

  const handlePointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return

    swipeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      isTracking: true,
      isHorizontal: false,
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (_) {
      // ignore
    }
  }

  const handlePointerMove = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (!st.isHorizontal) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      st.isHorizontal = Math.abs(dx) > Math.abs(dy)
    }

    if (st.isHorizontal) {
      e.preventDefault()
    }
  }

  const handlePointerEnd = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -40) {
        if (typeof onSwipeChange === 'function') onSwipeChange(true)
        else setUncontrolledIsSwiped(true)
      }
      if (dx > 30) {
        if (typeof onSwipeChange === 'function') onSwipeChange(false)
        else setUncontrolledIsSwiped(false)
      }
    }

    swipeRef.current.isTracking = false
    swipeRef.current.pointerId = null
  }

  return (
    <div
      className={cn(styles.card, { [styles.active]: isActive, [styles.swiped]: isSwipedValue })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div className={styles.cardInfo}>
        <div className={styles.headerRow}>
          <div className={styles.contactHeader}>
            <span className={styles.contactType}>{headerTitle}</span>

            {data.age !== null && <span className={styles.age}>({data.age} years old)</span>}
          </div>

          {setMain && (
            <div className={styles.nextOfKin}>
              <span>{mainLabel}</span>

              <Switcher onClick={setMain} id={data.id} isActive={data.is_main} />
            </div>
          )}
        </div>

        <div className={styles.fields}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Full name:</span>

            <UseSmartTooltip
              tagType={'span'}
              className={cn(styles.smartFieldTooltip, styles.fieldValue)}
              children={data.fullName}
              amount_of_line={2}
            />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Address:</span>
            <UseSmartTooltip
              tagType={'span'}
              className={cn(styles.smartFieldTooltip, styles.fieldValue)}
              children={data.address}
              amount_of_line={2}
            />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Contacts:</span>

            <UseSmartTooltip
              tagType={'span'}
              className={cn(styles.smartFieldTooltip, styles.fieldValue)}
              children={data?.contacts || '—'}
              amount_of_line={3}
            />
          </div>
        </div>
      </div>

      {data.id !== 'default' && (
        <div className={styles.cardControls}>
          <button
            className={cn(styles.cardControl, styles.cardControlEdit)}
            onClick={() => onEdit(data.id, isActive)}
            type='button'
          >
            {isActive ? <CloseIcon /> : <EditIcon />}
          </button>

          <button
            className={cn(styles.cardControl, styles.cardControlDelete)}
            onClick={() => onDelete(data.id, isActive)}
            type='button'
          >
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  )
})

export default RelativeContactsGroupItem
